import React, { useState, useRef, useEffect } from "react";
import { Row, Col, Input, Button, Modal, Typography, Alert } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { validOtp } from "../../../../services";
import messages from "../../../../errors/messages.en-IN.json";
import { message } from "antd";

const { Text, Title } = Typography;

const LoyaltyOTPVerificationModal = ({
  showOtpPopup,
  setShowOtpPopup,
  cart,
  loyaltyInputValue,
  checkLoyality,
  verifyLoyalty,
}) => {
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [otpError, setOtpError] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(false);
  const inputsRef = useRef([]);

  useEffect(() => {
    if (showOtpPopup) {
      setTimeout(() => {
        inputsRef.current[0]?.focus();
      }, 100);
    }
  }, [showOtpPopup]);

  const handleChange = (value, index) => {
    setOtpError(false);
    if (!/^[0-9]?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Focus next input
    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    setOtpError(false);
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const otpValue = otp.join("");
    try {
      const validOtpResponse = await validOtp(cart.customer.cwrCustomerId, otpValue, loyaltyInputValue, "loyalty");
      const status = validOtpResponse.data.data.validOtp.status;
      if (status === "200") {
        setVerificationStatus(true);
        setTimeout(() => {
          setOtp(Array(6).fill(""));
          setShowOtpPopup(false);
          setVerificationStatus(false);
          checkLoyality();
        }, 500);
      } else if (status === "201") {
        setOtpError(true);
      }
    } catch (err) {
      console.error(err);
      message.error(`${messages.info.invalid_otp}`);
    }
  };

  const handleResendOTP = () => {
    setResendTimer(30);
    setCanResend(false);
    setVerificationStatus(false);
    setOtp(Array(6).fill(""));
    verifyLoyalty();

    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  return (
    <Modal
      open={showOtpPopup}
      footer={null}
      closable={false}
      centered
      styles={{ body: { textAlign: "center", padding: "40px 24px" } }}
      style={{ borderRadius: 8 }}
      afterOpenChange={(open) => {
        if (open) {
          setTimeout(() => {
            inputsRef.current[0]?.focus();
          }, 50);
        }
      }}
    >
      <Title level={4} style={{ marginBottom: 8 }}>
        Verify OTP for Loyalty Redemption
      </Title>
      <Text strong style={{ fontSize: 16 }}>
        Customer: {cart?.customer?.loyaltyLevel?.name}
      </Text>
      <p contentEditable={false} suppressContentEditableWarning style={{ margin: "8px 0 16px", color: "rgba(0,0,0,0.65)" }}>
        Redeeming {loyaltyInputValue} loyalty points
      </p>
      {verificationStatus ? (
        <>
          <CheckCircleOutlined style={{ fontSize: "5rem", color: "green" }} />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Alert
              type="success"
              message={<span style={{ color: "#166534" }}>Loyalty points successfully redeemed! Transaction completed.</span>}
              showIcon
              style={{
                backgroundColor: "#f0fdf4",
                border: "1px solid #bbf7d0",
                marginBottom: 20,
                width: "70%",
                height: 70,
                borderRadius: "0.5rem",
                marginTop: "2vh",
              }}
            />
          </div>
        </>
      ) : (
        <>
          <p style={{ color: "rgba(0,0,0,0.65)", marginBottom: 24 }}>Enter the 6-digit OTP sent to the customer's registered mobile number</p>

          <Row justify="center" gutter={8} style={{ marginBottom: 28 }}>
            {otp.map((digit, idx) => (
              <Col key={idx}>
                <Input
                  autoFocus={idx === 0}
                  maxLength={1}
                  value={digit}
                  ref={(el) => (inputsRef.current[idx] = el)}
                  onChange={(e) => handleChange(e.target.value, idx)}
                  onKeyDown={(e) => handleKeyDown(e, idx)}
                  style={{
                    width: 50,
                    height: 50,
                    textAlign: "center",
                    fontSize: 20,
                    borderRadius: 4,
                  }}
                />
              </Col>
            ))}
          </Row>

          {otpError && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Alert
                type="error"
                message={<span style={{ color: "#991b1b" }}>Invalid OTP. Please check and try again.</span>}
                showIcon
                style={{
                  backgroundColor: "#fff1f0",
                  border: "1px solid #fecaca",
                  color: "#cf1322",
                  marginBottom: 20,
                  width: "70%",
                  height: 50,
                  borderRadius: "0.5rem",
                }}
              />
            </div>
          )}

          <Row justify="center" gutter={16}>
            <Col>
              <Button
                onClick={() => {
                  setShowOtpPopup(false);
                  setOtp(Array(6).fill(""));
                  setOtpError(false);
                }}
                style={{ width: 100 }}
              >
                Cancel
              </Button>
            </Col>
            <Col>
              <Button type="primary" onClick={handleVerify} style={{ width: 100 }} disabled={otp.includes("")}>
                Verify OTP
              </Button>
            </Col>
          </Row>
          <div style={{ marginTop: 16 }}>
            <Button type="link" onClick={handleResendOTP} disabled={!canResend}>
              {canResend ? "Resend OTP" : `Resend OTP in ${resendTimer}s`}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default LoyaltyOTPVerificationModal;
